// Runs — history list ported pixel-true from prototype RunsListPage.
// Mission-control hero with TelemetryRow summary stats, FilterStrip,
// search input, and a card-based list (NOT a table). Each card shows
// a status indicator, profile, volume/cloud chips, layer dots, status
// label, and contextual actions (+ Add for complete-with-DC, ↻ Refresh,
// ↻ Retry for failed).

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MissionControlHero } from "@/components/hero/MissionControlHero";
import { TelemetryRow } from "@/components/hero/TelemetryRow";
import { LayerDots } from "@/components/common/LayerDots";
import { api } from "@/lib/api";
import { useConfigStore } from "@/lib/store/configStore";
import { useUiStore } from "@/lib/store/uiStore";
import type { Run } from "@/lib/types";

type RunFilter =
  | "all"
  | "running"
  | "complete"
  // PROD-A3 (2026-04-21): partial-success bucket. Surfaced only when
  // at least one run carries ``status === "complete_with_errors"``.
  | "partial"
  | "failed"
  | "refreshable";

// True for jobs that drove a real pipeline (CRM provisioning or hyper
// TDSX). Everything else (AI pack drafts, Explorer reshapes, etc.)
// shares the `jobs` table but doesn't have Monitor telemetry, so we
// dim those rows and suppress the click-to-Monitor affordance.
//
// Surgical fix per docs/KNOWN_ISSUES.md option (C); the longer-term
// option (A) — bucket the page by job kind — is still in the backlog.
function isPipelineRun(run: Run): boolean {
  // No `kind` from the backend means pre-`job_type`-aware data; assume
  // pipeline so we don't accidentally hide history that predates this
  // change.
  if (!run.kind) return true;
  return run.kind === "provision" || run.kind === "story";
}

function describeNonPipelineKind(kind?: string): string {
  if (!kind) return "Other";
  if (kind === "ai_generate" || kind === "ai_generate_url") return "AI pack draft";
  if (kind === "ai_refine") return "AI refinement";
  if (kind === "ai_extract") return "AI extraction";
  if (kind === "ai_battlecard") return "AI battlecard";
  if (kind === "ai_demo_coach") return "AI demo coach";
  if (kind === "ai_playbook") return "AI playbook";
  if (kind === "reshape") return "Explorer reshape";
  return kind.replace(/_/g, " ");
}

export function Runs(): JSX.Element {
  const navigate = useNavigate();
  const pickMode = useConfigStore((s) => s.pickMode);
  const [filter, setFilter] = useState<RunFilter>("all");
  const [query, setQuery] = useState("");

  const { data: runs = [] } = useQuery({
    queryKey: ["runs"],
    queryFn: api.listRuns,
  });

  // Counts and telemetry intentionally only consider real pipeline rows
  // — a Run history page that says "21 total · 6 need attention" while
  // ⅓ of them are AI pack drafts is misleading. The non-pipeline rows
  // are still rendered in the list (just dimmed) so the user can audit
  // their full activity.
  const pipelineRuns = useMemo(() => runs.filter(isPipelineRun), [runs]);
  // PROD-A3 (2026-04-21): partial-success runs are counted under
  // ``complete`` so the green "Complete" filter still shows every
  // finished run, but they ALSO get their own ``partial`` bucket and
  // roll into ``need attention`` so the SE doesn't have to scroll
  // past confetti to spot them. Success-rate telemetry treats partial
  // as a *failure* — the analytics didn't deploy, that's not 100%.
  const counts = {
    all: pipelineRuns.length,
    running: pipelineRuns.filter((r) => r.status === "running").length,
    complete: pipelineRuns.filter(
      (r) => r.status === "complete" || r.status === "complete_with_errors",
    ).length,
    partial: pipelineRuns.filter((r) => r.status === "complete_with_errors").length,
    failed: pipelineRuns.filter((r) => r.status === "failed").length,
    refreshable: pipelineRuns.filter(
      (r) =>
        (r.status === "complete" || r.status === "complete_with_errors") &&
        r.layers.includes("dc"),
    ).length,
  };

  // Compute the telemetry rollup from the actual run history instead
  // of the placeholder numbers that shipped with the prototype. We only
  // include runs that actually finished (complete + failed) when looking
  // at duration / success rate so a stuck "running" doesn't skew it.
  const telemetry = useMemo(() => {
    const finished = pipelineRuns.filter(
      (r) =>
        r.status === "complete" ||
        r.status === "complete_with_errors" ||
        r.status === "failed",
    );
    const minsParsed = finished
      .map((r) => Number((r.duration ?? "").split(" ")[0]))
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b);
    const median = minsParsed.length
      ? minsParsed[Math.floor(minsParsed.length / 2)]
      : null;
    const successPct = finished.length
      ? Math.round(
          (finished.filter((r) => r.status === "complete").length /
            finished.length) *
            100,
        )
      : null;
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const thisWeek = pipelineRuns.filter((r) => {
      const t = r.startedAt ? Date.parse(r.startedAt) : NaN;
      return Number.isFinite(t) && t >= oneWeekAgo;
    }).length;
    const totalStories = pipelineRuns.reduce(
      (acc, r) => acc + (r.storiesCount ?? 0),
      0,
    );
    return { median, successPct, thisWeek, totalStories };
  }, [pipelineRuns]);

  const filtered = useMemo(() => {
    let r = runs;
    if (filter === "running") r = r.filter((x) => x.status === "running");
    else if (filter === "complete")
      r = r.filter(
        (x) => x.status === "complete" || x.status === "complete_with_errors",
      );
    else if (filter === "partial")
      r = r.filter((x) => x.status === "complete_with_errors");
    else if (filter === "failed") r = r.filter((x) => x.status === "failed");
    else if (filter === "refreshable")
      r = r.filter(
        (x) =>
          (x.status === "complete" || x.status === "complete_with_errors") &&
          x.layers.includes("dc"),
      );
    if (query) {
      const q = query.toLowerCase();
      r = r.filter(
        (x) =>
          (x.profile ?? "").toLowerCase().includes(q) ||
          (x.org ?? "").toLowerCase().includes(q),
      );
    }
    return r;
  }, [runs, filter, query]);

  const startNew = (): void => {
    pickMode("full");
    navigate("/configure");
  };

  const queryClient = useQueryClient();
  const showToast = useUiStore((s) => s.showToast);
  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelRun(id),
    onSuccess: () => {
      showToast("Run cancelled");
      void queryClient.invalidateQueries({ queryKey: ["runs"] });
    },
    onError: (e) => showToast(`Cancel failed: ${(e as Error).message}`),
  });

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb="Runs"
        title="Run history"
        subtitle={(() => {
          const needAttention = counts.failed + counts.partial;
          if (runs.length === pipelineRuns.length) {
            return `${pipelineRuns.length} total · ${counts.running} running · ${needAttention} need attention`;
          }
          return `${pipelineRuns.length} pipeline runs · ${counts.running} running · ${needAttention} need attention · ${runs.length - pipelineRuns.length} other`;
        })()}
        astro={{ pose: "happy", message: "Your build history" }}
        iconGlyph="◇"
        chips={[{ label: "Last 30 days", bg: "rgba(255,255,255,.14)" }]}
        rightSlot={
          <button
            className="btn hero-primary lg"
            style={{ whiteSpace: "nowrap" }}
            onClick={startNew}
          >
            Start new run →
          </button>
        }
      >
        <TelemetryRow
          cells={[
            { k: "Total runs", v: pipelineRuns.length.toLocaleString() },
            { k: "This week", v: telemetry.thisWeek },
            {
              k: "Median time",
              v: telemetry.median != null ? `${telemetry.median} min` : "—",
            },
            {
              k: "Success rate",
              v: telemetry.successPct != null ? `${telemetry.successPct}%` : "—",
            },
            { k: "Stories built", v: telemetry.totalStories.toLocaleString() },
          ]}
        />
      </MissionControlHero>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "20px 0 14px",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <FilterStrip
          active={filter}
          onClick={(id) => setFilter(id as RunFilter)}
          options={[
            { id: "all",         label: "All",                  count: counts.all },
            { id: "running",     label: "Running",              count: counts.running },
            { id: "complete",    label: "Complete",             count: counts.complete },
            // PROD-A3 (2026-04-21): the Partial filter is only
            // surfaced when at least one run actually landed in the
            // amber "completed with errors" state — otherwise we
            // hide the chip to keep the strip compact.
            ...(counts.partial > 0
              ? [
                  {
                    id: "partial" as const,
                    label: "Need attention",
                    count: counts.partial,
                  },
                ]
              : []),
            { id: "failed",      label: "Failed",               count: counts.failed },
            { id: "refreshable", label: "Refreshable",          count: counts.refreshable },
          ]}
        />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search profile or org…"
          style={{
            padding: "8px 12px",
            border: "1px solid var(--bd)",
            borderRadius: 8,
            fontSize: 12,
            outline: "none",
            width: 240,
            background: "white",
          }}
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No runs match"
          blurb="Try a different filter or clear the search."
          cta={
            <button
              className="btn ghost sm"
              onClick={() => {
                setFilter("all");
                setQuery("");
              }}
            >
              Clear filters
            </button>
          }
        />
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {filtered.map((r) => {
            const pipeline = isPipelineRun(r);
            return (
              <RunCard
                key={r.id}
                run={r}
                pipeline={pipeline}
                onOpen={() => {
                  // Non-pipeline rows have no Monitor / RunDetail
                  // telemetry to show. Route AI drafts back to the
                  // Profiles page where the pack lives; everything
                  // else is just informational.
                  if (!pipeline) {
                    if (r.kind?.startsWith("ai_") && r.profileId) {
                      navigate(`/profiles/${r.profileId}`);
                    }
                    return;
                  }
                  navigate(`/runs/${r.id}`);
                }}
                onLayer={() => {
                  pickMode("continue", r.id);
                  navigate("/configure");
                }}
                onRefresh={() => {
                  pickMode("refresh", r.id);
                  navigate("/configure");
                }}
                onCancel={() => {
                  if (confirm("Cancel this run?")) cancel.mutate(r.id);
                }}
                onMonitor={() => navigate(`/monitor/${r.id}`)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterStrip({
  options,
  active,
  onClick,
}: {
  options: { id: string; label: string; count?: number }[];
  active: string;
  onClick: (id: string) => void;
}): JSX.Element {
  return (
    <div
      style={{
        display: "inline-flex",
        background: "white",
        border: "1px solid var(--bd)",
        borderRadius: 10,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((opt) => {
        const on = opt.id === active;
        return (
          <button
            key={opt.id}
            onClick={() => onClick(opt.id)}
            style={{
              padding: "7px 14px",
              borderRadius: 7,
              border: 0,
              background: on ? "var(--eb-50)" : "transparent",
              color: on ? "white" : "var(--ink-2)",
              fontSize: 12,
              fontWeight: on ? 600 : 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            {opt.label}
            {opt.count !== undefined && (
              <span
                style={{
                  padding: "1px 7px",
                  borderRadius: 999,
                  fontSize: 10,
                  background: on ? "rgba(255,255,255,.2)" : "var(--cb-95)",
                  color: on ? "white" : "var(--ink-3)",
                  fontWeight: 600,
                }}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface RunCardProps {
  run: Run;
  pipeline: boolean;
  onOpen: () => void;
  onLayer: () => void;
  onRefresh: () => void;
  onCancel: () => void;
  onMonitor: () => void;
}

function RunCard({ run, pipeline, onOpen, onLayer, onRefresh, onCancel, onMonitor }: RunCardProps): JSX.Element {
  const cfg = STATUS_CFG[run.status];
  // De-emphasized container for non-pipeline rows: lower opacity, no
  // hover lift, and a "AI pack draft" / "Reshape" pill to make the
  // category obvious instead of looking like a half-broken pipeline.
  return (
    <button
      onClick={onOpen}
      className="card"
      style={{
        padding: "14px 18px",
        textAlign: "left",
        cursor: pipeline ? "pointer" : "default",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto auto auto",
        gap: 20,
        alignItems: "center",
        opacity: pipeline ? 1 : 0.65,
        background: pipeline ? undefined : "var(--cb-99, #F8FAFC)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            flexShrink: 0,
            background: cfg.bg,
            color: cfg.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            className={cfg.pulse ? "pulse-dot" : ""}
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: cfg.dot,
            }}
          />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--ink)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {run.profile ?? "Custom"}
              </div>
              {!pipeline && (
                <span
                  className="chip ghost"
                  style={{
                    fontSize: 10,
                    padding: "1px 7px",
                    flexShrink: 0,
                    background: "var(--cb-95, #E2E8F0)",
                    color: "var(--ink-2)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                  }}
                  title="Not a pipeline run — Monitor telemetry isn't applicable here."
                >
                  {describeNonPipelineKind(run.kind)}
                </span>
              )}
              <span
                className="chip ghost"
                style={{ fontSize: 10, padding: "1px 7px", flexShrink: 0 }}
              >
                {run.volume}
              </span>
              {pipeline && (
                <span
                  className="chip ghost"
                  style={{ fontSize: 10, padding: "1px 7px", flexShrink: 0 }}
                >
                  {run.cloud}
                </span>
              )}
            </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--ink-3)",
              marginTop: 3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {run.org} · {run.startedAt ?? run.completed} · {run.duration} ·{" "}
            {run.storiesCount} stories · {run.owner}
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 4,
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: "var(--ink-3)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
            fontWeight: 600,
          }}
        >
          Layers
        </div>
        <LayerDots layers={run.layers} size="lg" />
      </div>
      <span style={{ fontSize: 11, color: cfg.fg, fontWeight: 600 }}>
        {cfg.label}
      </span>
      <div style={{ display: "flex", gap: 4 }}>
        {pipeline &&
          (run.status === "complete" || run.status === "complete_with_errors") && (
            <>
              {run.layers.includes("tdsx") && (
                <a
                  className="btn ghost sm"
                  href={`/api/jobs/${run.id}/download-hyper`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title="Download Tableau Packaged Datasource"
                  style={{ textDecoration: "none" }}
                >
                  ↓ TDSX
                </a>
              )}
              {/* Partial-success runs get a Review button instead of
                  the +Add affordance — the analytics layer didn't land,
                  so the next move is to look at what failed, not to
                  layer more on top. */}
              {run.status === "complete_with_errors" ? (
                <button
                  className="btn ghost sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMonitor();
                  }}
                  title="Review deployment errors"
                >
                  ⚠ Review
                </button>
              ) : (
                <button
                  className="btn ghost sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLayer();
                  }}
                  title="Layer on Data 360, apps, etc."
                >
                  + Add
                </button>
              )}
              {run.layers.includes("dc") && (
                <button
                  className="btn ghost sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRefresh();
                  }}
                  title="Refresh streams"
                >
                  ↻
                </button>
              )}
            </>
          )}
        {pipeline && run.status === "failed" && (
          <button
            className="btn ghost sm"
            onClick={(e) => {
              e.stopPropagation();
              onLayer();
            }}
          >
            ↻ Retry
          </button>
        )}
        {pipeline && (run.status === "running" || run.status === "queued") && (
          <>
            <button
              className="btn ghost sm"
              onClick={(e) => {
                e.stopPropagation();
                onMonitor();
              }}
            >
              Watch →
            </button>
            <button
              className="btn danger sm"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </button>
  );
}

// PROD-A3 (2026-04-21): ``complete_with_errors`` gets its own amber
// chip so the run history doesn't show a green dot for jobs whose
// analytics layer never deployed. ``var(--orange)`` lines up with the
// orange-bordered DeployErrorsPanel on the Monitor page so the SE sees
// a consistent treatment from list → detail → monitor.
const STATUS_CFG: Record<
  Run["status"],
  { bg: string; fg: string; dot: string; pulse: boolean; label: string }
> = {
  running:               { bg: "#fef4dc", fg: "#8a5200",       dot: "#d97706",       pulse: true,  label: "Running" },
  complete:              { bg: "#e6f3eb", fg: "var(--teal)",   dot: "var(--teal)",   pulse: false, label: "Complete" },
  complete_with_errors:  { bg: "rgba(243,154,55,.12)", fg: "var(--orange)", dot: "var(--orange)", pulse: false, label: "⚠ With errors" },
  failed:                { bg: "#fbe8eb", fg: "var(--pink)",   dot: "var(--pink)",   pulse: false, label: "Failed" },
  queued:                { bg: "#e5f2f9", fg: "#2e5a8c",       dot: "#2e5a8c",       pulse: false, label: "Queued" },
  cancelled:             { bg: "#eef1f5", fg: "var(--ink-3)",  dot: "var(--ink-3)",  pulse: false, label: "Cancelled" },
};

function EmptyState({
  title,
  blurb,
  cta,
}: {
  title: string;
  blurb?: string;
  cta?: JSX.Element;
}): JSX.Element {
  return (
    <div
      style={{
        padding: 48,
        textAlign: "center",
        background: "var(--cb-98)",
        borderRadius: 12,
        border: "1px dashed var(--bd)",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 56,
          height: 56,
          borderRadius: 14,
          background: "white",
          border: "1px solid var(--bd)",
          fontSize: 24,
          color: "var(--ink-3)",
          marginBottom: 14,
        }}
      >
        ◇
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink)" }}>
        {title}
      </div>
      {blurb && (
        <div
          style={{
            fontSize: 13,
            color: "var(--ink-2)",
            maxWidth: 440,
            margin: "6px auto 0",
            lineHeight: 1.5,
          }}
        >
          {blurb}
        </div>
      )}
      {cta && <div style={{ marginTop: 20 }}>{cta}</div>}
    </div>
  );
}
